import React, {useEffect, useState, useRef} from 'react';
import PropTypes from "prop-types";
import CsfMembreUtils from "../utils/CsfMembreUtils";
import { resetErrMessages } from '../../csf-ufc-fournisseur/includes/utils';
import moment from 'moment';
import JustValidate from 'just-validate';
import { InputMask } from 'primereact/inputmask';

export default function CsfMemberEditActivitiesModal({modalKey, selectedActivity = {}, ufcCase = {}, activitiesDataLayer = {}, shouldEmptyModal, setShouldEmptyModal}){
  const [isError, setIsError] = useState(false);
  const [isActivityError, setIsActivityError] = useState(false);
  const dismissButton = useRef();
  const [recognitionNumber, setRecognitionNumber] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [validator, setValidator] = useState(null);
  const [activityName, setActivityName] = useState('');
  const [activityUfc, setActivityUfc] = useState('');
  const selectedActivityRef = useRef();
  const [activityId, setActivityId] = useState('');
  const [changed, setChanged] = useState('');

  // selectedActivity changes so many times, had to add an if :/
  useEffect(() => {
    // if selectedActivity is not empty
    if (selectedActivity?.Recognition_Code) {
      selectedActivityRef.current = selectedActivity;

      setRecognitionNumber(selectedActivity?.Recognition_Code || '');
      setActivityDate(selectedActivity?.Suivi_Le ? moment(selectedActivity?.Suivi_Le, "YYYY-MM-DD").format("YYYY-MM-DD") : '');
      setActivityName(selectedActivity?.Activite_Id__Rn_Descriptor || null);
      setActivityUfc(selectedActivity?.SousTotal || null);
    }
  }, [selectedActivity]);

  useEffect(() => {
    setIsError(false);
    resetErrMessages();

    if (shouldEmptyModal) {
      setShouldEmptyModal(false);
      setRecognitionNumber('');
      setActivityDate('');
      setActivityName('');
      setActivityUfc('');
    }

    //Reput information if its the same modal
    if (selectedActivity?.Recognition_Code) {
      setRecognitionNumber(selectedActivityRef.current?.Recognition_Code || '');
      setActivityDate(selectedActivityRef.current?.Suivi_Le ? moment(selectedActivityRef.current?.Suivi_Le, "YYYY-MM-DD").format("YYYY-MM-DD") : '');
      setActivityName(selectedActivityRef.current?.Activite_Id__Rn_Descriptor || null)
      setActivityUfc(selectedActivityRef.current?.SousTotal || null)
    }
  }, [shouldEmptyModal]);

  // Get activity Name from reco number
  async function updateActivityName() {
    const activity = await activitiesDataLayer.getActivity(recognitionNumber, isDateComplete(activityDate) ? activityDate : '', setIsActivityError);

    setActivityName(activity?.REDU_Activity_Id__Rn_Descriptor);
    setActivityId(activity?.REDU_Activity_Id);
  }

  // Get activity UFC from activity date and activity id
  async function updateActivityUfc() {
    const activityUfc = await activitiesDataLayer.getActivityUfc(activityId, activityDate, setIsActivityError);

    setActivityUfc(activityUfc?.CurrentUserUFC);
  }

  useEffect(() => {
    setIsError(false);
    setIsActivityError(false);

    if (changed) {
      setChanged(false);

      if (activityDate !== '' && !moment(activityDate, "YYYY-MM-DD", true).isValid()) {
        return;
      };

      if (isRecoNumberComplete(recognitionNumber)) {
        updateActivityName();
      } else {
        // reset
        setActivityName('');
        setActivityUfc('');
        setActivityId('');
      }

      if(isRecoNumberComplete(recognitionNumber) && isDateComplete(activityDate) && activityId) {
        updateActivityUfc();
      } else {
        // reset
        setActivityUfc('');
      }
    }

  }, [recognitionNumber, activityDate]);

  useEffect(() => {
    // Update the ufc once the activity Id is set
    if (isRecoNumberComplete(recognitionNumber) && isDateComplete(activityDate) && activityId) {
      updateActivityUfc();
    }
  }, [activityId]);

  function isRecoNumberComplete(recognitionNumber) {
    const regex = /(CSF|csf)\d{2}-\d{2}-\d{5}/g; //CSFxx-xx-xxxxx
    return recognitionNumber.match(regex);
  }

  function isDateComplete(activityDate) {
    const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/g;
    return activityDate.match(dateRegex);
  }

  useEffect(() => {
    if (isActivityError) {
      setActivityName('');
      setActivityUfc('');
    }
  }, [isActivityError]);

  /*
  * Setup form validation
  * */
  useEffect(() => {
    const validator = new JustValidate(`#membreActivitiesModal-${modalKey}`);

    validator
      .addField(`#activities-reco-${modalKey}`, [
        {
          rule: 'required',
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required'),
        },
      ])
      .addField(`#activities-date-${modalKey}`, [
        {
          rule: 'required',
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required'),
        },
      ])
    setValidator(validator);

    return () => validator.destroy();
  }, []);

  function createEditActivity(e) {
    e.preventDefault()

    if (validator) {
      validator.validate();
      setTimeout(() => {
        if (validator?.isValid) {
          activitiesDataLayer.createOrUpdateActivity(selectedActivity, ufcCase, recognitionNumber, activityDate, setIsError, dismissButton);
        } else {
          console.error('Form validation failed!');
        }
      });
    }
  }

  return (
    <div className="modal fade" id={`membreActivitiesModal-${modalKey}`} tabIndex="-1" role="dialog" aria-labelledby="membreActivitiesModalTitle"
         aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <form className="modal-content" onSubmit={createEditActivity}>
          <div className="modal-header d-flex align-items-center">
            <h2 id="membreActivitiesModalTitle">{window.Drupal.t('membre.activities.modal.title')}</h2>
            <button type="button" className="close" data-dismiss="modal" ref={dismissButton}
              aria-label={window.Drupal.t('lang.modal.close')}>
              <i className="icon icon-csf-close-noborder"><span className="d-none">&nbsp;</span></i>
            </button>
          </div>

          <div className="modal-body">
            {isError === true && (
              <div className="alert alert-danger" role="alert"
                aria-live="polite">{window.Drupal.t('broker.manager.error.message')}</div>
            ) }
            {isActivityError === true && (
              <div className="alert alert-danger" role="alert"
                aria-live="polite">{window.Drupal.t('membre.activities.notFound')}</div>
            ) }
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label htmlFor={`activities-reco-${modalKey}`}>{window.Drupal.t('fournisseur.recognition.detail.details.numlabel')}*</label>
                  <InputMask
                    type="text"
                    id={`activities-reco-${modalKey}`}
                    name={`activities-reco-${modalKey}`}
                    value={recognitionNumber}
                    //pattern="CSF\d{2}-\d{2}-\d{5}"
                    placeholder="CSFXX-XX-XXXXX"
                    mask="CSF99-99-99999"
                    onChange={e => {setRecognitionNumber(e.target.value); setChanged(true)}}
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-group date">
                  <label htmlFor={`activities-date-${modalKey}`}>{window.Drupal.t('membre.activities.attendanceDate')}*</label>
                  <input
                    type="date"
                    id={`activities-date-${modalKey}`}
                    name={`activities-date-${modalKey}`}
                    value={activityDate}
                    onChange={e => {setActivityDate(moment(e.target.value, "YYYY-MM-DD").format("YYYY-MM-DD")); setChanged(true)}}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-9">
                <div className="form-group form-group-tall">
                  <label htmlFor="activities-name">{window.Drupal.t('membre.activities.activityName')}</label>
                  <input
                    type="text"
                    id="activities-name"
                    name="activities-name"
                    value={activityName || ''}
                    disabled
                  />
                  {activityName &&
                  <div className="activities__formatted-input">
                    <span className="name__main">{CsfMembreUtils.limitCharacter(activityName, 60)}</span><br /> {/* BE: langue courante*/}
                    <span className="name__secondary">Implementation of insurance services</span> {/* BE: traduction */}
                  </div>
                  }
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className="form-group form-group-tall">
                  <label htmlFor="activities-ufc">{window.Drupal.t('PDU')}</label>
                  <input
                    type="text"
                    id="activities-ufc"
                    name="activities-ufc"
                    value={activityUfc || ''}
                    disabled
                  />
                </div>
              </div>
            </div>

            <div className="mt-1">
              <p className="m-0" dangerouslySetInnerHTML={{ __html: window.Drupal.t('membre.activities.modal.info')}}></p>
            </div>

            <div className="d-flex align-items-center mt-3 mb-3">
              <img className="flex-shrink-0 mr-2" src="/themes/custom/tink/dist/assets/icon-membre-warning.svg" alt="" />
              <p className="larger-text m-0 text-primary">{window.Drupal.t('membre.activities.modal.warning')}</p>
            </div>
          </div>

          <div className="modal-footer">
              <div className="space-between-wrapper">
                {!selectedActivity.Recognition_Code && (
                  <button type="button" className='button btn-outlined btn-larger' data-dismiss="modal">{window.Drupal.t('fournisseur.back.btn')}</button>
                )}
                <input
                  className="btn-submit btn-pale btn-larger"
                  type="submit"
                  value={window.Drupal.t('fournisseur.gestion.submit.label')}
                  disabled={activityName === ''}
                />
                {selectedActivity.Recognition_Code && (
                  <button type="button" className='button btn-red btn-larger' data-dismiss="modal"
                    data-toggle="modal" data-target="#membreDeleteActivitiesModal">{window.Drupal.t('delete.btn')}</button>
                )}
              </div>
            </div>
        </form>
      </div>
    </div>
  )
}

CsfMemberEditActivitiesModal.propTypes = {
  modalKey: PropTypes.string,
  selectedActivity: PropTypes.object,
  ufcCase: PropTypes.object,
  activitiesDataLayer: PropTypes.object,
  shouldEmptyModal: PropTypes.bool,
  setShouldEmptyModal: PropTypes.func,
};
